import { createSlice } from '@reduxjs/toolkit';
import { axiosAmApiInstance as amApi } from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';
import {
  CreateOrderTypeRequest,
  OrderTypesState,
  UpdateOrderTypeRequest
} from '../../@types/orderTypes';
import { translator } from 'translation/useTranslator';

const initialState: OrderTypesState = {
  loading: {},
  orderTypes: [],
  fetched: false
};

const slice = createSlice({
  name: 'orderTypes',
  initialState,
  reducers: {
    startLoading(state, action) {
      state.loading[action.payload] = true;
    },
    stopLoading(state, action) {
      state.loading[action.payload] = false;
    },
    getOrderTypesSuccess(state, action) {
      state.orderTypes = action.payload;
      state.fetched = true;
      state.loading.orderTypes = false;
    }
  }
});

export function getOrderTypes() {
  return async () => {
    dispatch(slice.actions.startLoading('orderTypes'));
    try {
      const response = await amApi.get('/order-types');
      dispatch(slice.actions.getOrderTypesSuccess(response.data?.orderTypes ?? []));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.orderTypesFetched() ?? 'Order types fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('orderTypes'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrderTypes() ?? 'Could not fetch the order types'
      });
    }
  };
}

export function createOrderType(settings: CreateOrderTypeRequest) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      const response = await amApi.post('/order-types', settings);
      dispatch(getOrderTypes());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.orderTypeCreated() ?? 'Order type created'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotCreateOrderType() ?? 'Could not create the order type'
      });
    }
  };
}

export function updateOrderType({
  id,
  settings
}: {
  id: string;
  settings: UpdateOrderTypeRequest;
}) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      const response = await amApi.put(`/order-types/${id}`, settings);
      dispatch(getOrderTypes());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.orderTypeUpdated() ?? 'Order type updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUpdateOrderType() ?? 'Could not update the order type'
      });
    }
  };
}

export default slice.reducer;
